import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Radio, Home, Compass, Video, MessageCircle, Plus, LogOut, User, Crown, Wallet, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { mediaUrl } from "@/lib/api";
import SearchBar from "@/components/SearchBar";
import NotificationBell from "@/components/NotificationBell";
import VerifiedBadge from "@/components/VerifiedBadge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/feed", label: "Feed", icon: Home },
  { to: "/discover", label: "Discover", icon: Compass },
  { to: "/videos", label: "Videos", icon: Video },
  { to: "/messages", label: "Messages", icon: MessageCircle },
];

export default function Navbar({ onUpload }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/feed" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <span className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/40 glow-emerald">
            <Radio className="w-5 h-5 text-emerald-400" />
          </span>
          <span className="font-display font-extrabold text-lg tracking-tight">NEXT<span className="text-emerald-400">UP</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active = location.pathname === to;
            return (
              <Link key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  active ? "bg-white/10 text-emerald-400" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}>
                <Icon className="w-4 h-4" /> {label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <SearchBar />
          <button onClick={onUpload} data-testid="upload-open-btn"
            className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm transition-colors">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Upload</span>
          </button>

          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="profile-menu-btn" className="flex items-center gap-2 rounded-full p-0.5 border border-white/10 hover:border-emerald-500/50 transition-colors">
                {user?.avatar_url ? (
                  <img src={mediaUrl(user.avatar_url)} alt="me" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-300 font-bold text-sm">
                    {user?.artist_name?.[0]?.toUpperCase() || "A"}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#181824] border-white/10 text-white w-56">
              <div className="px-2 py-2">
                <p className="text-sm font-bold truncate flex items-center gap-1">{user?.artist_name}<VerifiedBadge show={user?.is_subscribed} size={14} /></p>
                <p className="text-xs text-zinc-500 font-mono-nu">@{user?.handle}</p>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="menu-my-profile" onClick={() => navigate(`/artist/${user.id}`)} className="cursor-pointer focus:bg-white/10">
                <User className="w-4 h-4 mr-2" /> My Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-earnings" onClick={() => navigate("/earnings")} className="cursor-pointer focus:bg-white/10">
                <Wallet className="w-4 h-4 mr-2" /> Earnings
              </DropdownMenuItem>
              {user?.role === "owner" && (
                <DropdownMenuItem data-testid="menu-owner" onClick={() => navigate("/owner")} className="cursor-pointer focus:bg-white/10 text-amber-400">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Owner Dashboard
                </DropdownMenuItem>
              )}
              {!user?.is_subscribed && (
                <DropdownMenuItem data-testid="menu-subscribe" onClick={() => navigate("/subscribe")} className="cursor-pointer focus:bg-white/10 text-amber-400">
                  <Crown className="w-4 h-4 mr-2" /> Get Artist Pass
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer focus:bg-white/10 text-red-400">
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <nav className="md:hidden flex items-center justify-around border-t border-white/10 px-2 py-1">
        {NAV.map(({ to, label, icon: Icon }) => {
          const active = location.pathname === to;
          return (
            <Link key={to} to={to} data-testid={`mobile-nav-${label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold ${active ? "text-emerald-400" : "text-zinc-500"}`}>
              <Icon className="w-5 h-5" /> {label}
            </Link>
          );
        })}
      </nav>
    </header>
  );
}
